import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { SearchSuggest } from "../components/SearchSuggest";

export const GRADE_STYLE = {
  great: "text-[#22c55e] font-bold drop-shadow-[0_0_6px_rgba(34,197,94,0.55)]",
  ok: "text-[#38bdf8] font-semibold",
  work: "text-white/80",
};

export const ZONE_COLS = [
  ["matchup", "Matchup"], ["ceiling", "Ceiling"], ["zone", "Zone%"], ["form", "Form%"],
  ["iso", "ISO"], ["xwoba", "xwOBA"], ["xwobac", "xwOBAcon"], ["sws", "SwS%"],
  ["pbrl", "pBRL%"], ["brl", "BRL%"], ["swsp", "SwSp%"], ["fb", "FB%"], ["hh", "HH%"], ["la", "LA"],
];

export function ZoneStatGrid({ stats }) {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
      {ZONE_COLS.map(([key, label]) => (
        <div key={key} className="bg-black/50 border border-white/10 rounded p-1.5 text-center">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
          <div className={`font-data text-xs sm:text-sm mt-0.5 ${GRADE_STYLE[stats[key]?.grade] || GRADE_STYLE.work}`}>{stats[key]?.value ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

export default function BatterZone() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["batterZone"], queryFn: () => api.get("/batter-zone").then((r) => r.data) });
  const all = [];
  const seenIds = new Set();
  for (const r of data?.rows || []) {
    if (!seenIds.has(r.mlb_id)) { seenIds.add(r.mlb_id); all.push(r); }
  }
  const rows = (q.trim() ? all.filter((r) => `${r.name} ${r.team}`.toLowerCase().includes(q.trim().toLowerCase())) : all).slice(0, 60);

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Batter <span className="gold-text">Zone</span></h1>
        <p className="text-neutral-500 text-sm mt-1">The full advanced sheet for every bat. <span className="text-[#22c55e] font-bold">Green = elite</span>, <span className="text-[#38bdf8] font-semibold">blue = solid</span>, white = needs work.</p>
      </div>
      <div className="fade-up-1">
        <SearchSuggest value={q} onChange={setQ} testid="zone-search-input"
          options={[...all.map((r) => r.name), ...new Set(all.map((r) => r.team))]}
          placeholder="Search a batter or team..." />
      </div>
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Loading the zone...</div>}
      <div className="space-y-2.5 fade-up-1">
        {rows.map((r) => (
          <div key={r.mlb_id} className="card-surface rounded-lg p-3" data-testid={`zone-row-${r.mlb_id}`}>
            <div className="flex items-center gap-3 mb-2">
              <img src={r.headshot} alt="" className="w-9 h-9 rounded-full object-cover bg-white/5 shrink-0" loading="lazy" />
              <div className="min-w-0 flex-1">
                <Link to={`/research/${r.mlb_id}`} className="font-semibold text-sm hover:text-[#a855f7] transition-colors duration-200">{r.name}</Link>
                <div className="text-[10px] text-neutral-500 truncate">{r.team} · vs {r.pitcher}</div>
              </div>
              <div className={`font-data text-lg font-bold ${r.confidence >= 7 ? "text-[#22c55e]" : "text-[#f59e0b]"}`}>{r.confidence}</div>
            </div>
            <ZoneStatGrid stats={r.stats} />
          </div>
        ))}
        {rows.length === 0 && !isLoading && <p className="text-neutral-500 text-sm" data-testid="zone-search-empty">No batter matches "{q}".</p>}
      </div>
      {data?.locked && <UpgradeGate feature={`The full Batter Zone sheet (${data.total} bats)`} />}
      <p className="text-[11px] text-neutral-600">xwOBA, SwS%, Zone% and SwSp% are model estimates blended from official MLB stats. Ceiling grades A+ to D rate raw HR upside.</p>
    </div>
  );
}
